import { useFlags } from 'launchdarkly-react-client-sdk';
import { capitalize, cloneDeep } from 'lodash-es';
import { UseFormReturn } from 'react-hook-form';
import { buildFormatterFormField } from '../../../components/FormatterSelect/FormatterSelect';
import {
  defaultFormatterIdForRenderer,
  formatterModelsByTypeAtom,
} from '../../../components/FormatterSelect/formatter-options-state';
import { useGetKPIGroupField } from '../../../data-fields/KPIFields';
import { sortGenericSortable } from '../../../data-models/app-config.data-model';
import { KpiConfigPeriod, KpiConfigPeriodToName } from '../../../data-models/company-financials.data-model';
import { RendererType } from '../../../data-models/field-definition';
import {
  FieldEntity,
  IField,
  ISimpleChoice,
  isNumericRenderer,
  KpiConfigScenario,
  KpiConfigScenarioToName,
  KpiConfigSection,
  rendererToFormatterId,
} from '../../../data-models/field2.data-model';
import {
  createField3DataModel,
  field3ToFormField,
  IField3,
  ISelectMeta,
  ITextMeta,
} from '../../../data-models/field3.data-model';
import { KPIGroup } from '../../../schemas/kpi-group.schema';
import { clientConfigFieldsAtom } from '../../../services/state/AppConfigStateJ';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { getForesightStore } from '../../../util/jotai-store';
import { IForm } from '../../../view-models/form.view-model';

const allFormatOptions = [
  { value: RendererType.currency, displayName: 'currency', secondaryLabel: '$123' },
  { value: RendererType.date, displayName: 'date', secondaryLabel: 'yyyy-MM-dd' },
  { value: RendererType.integer, displayName: 'integer', secondaryLabel: '123' },
  { value: RendererType.numeric, displayName: 'number', secondaryLabel: '123.45' },
  { value: RendererType.multiplier, displayName: 'multiplier', secondaryLabel: '123X' },
  { value: RendererType.percent, displayName: 'percent', secondaryLabel: '123%' },
  { value: RendererType.text, displayName: 'text', secondaryLabel: 'Text' },
];

export const KPINoGroupName = 'No Group';

function getAggFuncField() {
  return createField3DataModel<ISelectMeta<string>>({
    id: 8,
    description: 'Aggregation for the KPI column in the Portfolio Reporting grid',
    entity: FieldEntity.universalKPI,
    entityKey: 'gridMeta.aggFunc',
    displayName: 'Column Aggregation',
    format: RendererType.singleSelect,
    meta: {
      values: ['avg', 'sum'].map((value) => ({
        value,
        displayName: value.toUpperCase(),
      })),
      formatValueToLabel: true,
    },
  });
}

export function useGetKpiFields(): IField3<unknown>[] {
  const { showAggFuncInKpiConfig } = useFlags<MaggieFeatureFlags>();
  const getGroupField = useGetKPIGroupField();

  const fields: IField3<unknown>[] = [
    createField3DataModel<ITextMeta>({
      id: 0,
      entity: FieldEntity.universalKPI,
      entityKey: 'displayName',
      displayName: 'Name',
      format: RendererType.text,
      required: true,
      meta: {
        maxLength: -1,
      },
    }),
    createField3DataModel<ITextMeta>({
      id: 1,
      entity: FieldEntity.universalKPI,
      entityKey: 'description',
      displayName: 'Description',
      format: RendererType.text,
      meta: {
        maxLength: -1,
      },
    }),
    createField3DataModel<ISelectMeta<string>>({
      id: 2,
      entity: FieldEntity.universalKPI,
      entityKey: 'entity',
      format: RendererType.singleSelect,
      displayName: 'Category',
      required: true,
      meta: {
        multi: false,
        values: [
          { value: FieldEntity.universalKPI, displayName: 'Universal KPI' },
          { value: FieldEntity.userKPI, displayName: 'Custom KPI' },
          { value: FieldEntity.systemKPI, displayName: 'System KPI', disabled: true },
        ],
        formatValueToLabel: true,
      },
    }),
    createField3DataModel<ISelectMeta<RendererType>>({
      id: 3,
      entity: FieldEntity.universalKPI,
      entityKey: 'formMeta.renderer.type',
      displayName: 'Type',
      format: RendererType.singleSelect,
      required: true,
      meta: {
        multi: false,
        values: allFormatOptions,
        formatValueToLabel: true,
      },
    }),
    // Placeholder holding the Format field's slot right under Type. syncFormatterField swaps in
    // the real options once the chosen Type says which formatter family applies, or drops it for
    // a Type that has none.
    createField3DataModel<ISelectMeta<string>>({
      id: 9,
      entity: FieldEntity.universalKPI,
      entityKey: formatterFieldPath,
      displayName: 'Format',
      format: RendererType.singleSelect,
      meta: { multi: false, values: [] },
    }),
  ];

  fields.push(getGroupField('kpiGroupId', 'Group'));
  fields.push(
    createField3DataModel<ISelectMeta<string>>({
      id: 5,
      entity: FieldEntity.universalKPI,
      entityKey: 'sectionType',
      displayName: 'Scenario',
      format: RendererType.singleSelect,
      meta: {
        multi: false,
        values: Object.values(KpiConfigSection).map((value) => ({ value, displayName: capitalize(value) })),
        formatValueToLabel: true,
      },
    }),
    createField3DataModel<ISelectMeta<string>>({
      id: 6,
      description:
        "Accrued KPIs are aggregated for quarterly and yearly totals, while End of Period KPIs use the most recent month's value for these totals",
      entity: FieldEntity.universalKPI,
      entityKey: 'kpiScenario',
      displayName: 'Aggregation',
      format: RendererType.singleSelect,
      meta: {
        multi: false,
        values: Object.entries(KpiConfigScenarioToName).map(([key, value]) => ({
          value: key,
          displayName: value,
        })),
        formatValueToLabel: true,
      },
    }),
    createField3DataModel<ISelectMeta<string>>({
      id: 7,
      description:
        'Please choose the periods that you want to display for this KPI in the portfolio reporting grid',
      entity: FieldEntity.universalKPI,
      entityKey: 'periods',
      displayName: 'Period',
      format: RendererType.multiSelect,
      meta: {
        multi: false,
        values: Object.entries(KpiConfigPeriodToName).map(([key, value]) => ({
          value: key,
          displayName: value,
        })),
        formatValueToLabel: true,
      },
    })
  );
  if (showAggFuncInKpiConfig) {
    fields.push(getAggFuncField());
  }

  return fields;
}

export function getKPIsByGroupSorted(kpis: IField<unknown>[], kpiGroups: Map<number, KPIGroup>) {
  const kpisByGroup = kpis.reduce(
    (res, kpi) => {
      const kpiGroup = kpi.kpiGroupId ? kpiGroups.get(kpi.kpiGroupId) : undefined;
      const groupName = kpiGroup?.name ?? KPINoGroupName;

      if (res[groupName]) {
        res[groupName].push(kpi);
      } else {
        res[groupName] = [kpi];
      }
      return res;
    },
    {} as Record<string, IField<unknown>[]>
  );

  Object.values(kpisByGroup).forEach((kpis) => kpis.sort(sortGenericSortable));

  return kpisByGroup;
}

export const allowedValuesForEndOfPeriod = new Set<KpiConfigPeriod>([
  KpiConfigPeriod.GrowthMonth,
  KpiConfigPeriod.GrowthQuarter,
  KpiConfigPeriod.GrowthYear,
  KpiConfigPeriod.Latest,
]);
const allowedValuesForOtherScenarios = new Set<KpiConfigPeriod>(
  Object.values(KpiConfigPeriod).filter((v) => v !== KpiConfigPeriod.Latest)
);
export const formatFieldPath = 'formMeta.renderer.type';
/** The KPI's chosen display format; absent means "whatever the Type derives". */
export const formatterFieldPath = 'formMeta.formatter.id';

/**
 * Drops the Format field, which is form-only: its options are filled per-Type by the dynamic
 * transformer, so as a static grid column it would render raw formatter ids like `currency0Dp`.
 */
export function withoutFormatterField(fields: IField3<unknown>[]) {
  return fields.filter((field) => field.entityKey !== formatterFieldPath);
}
export const SystemKpiUneditableFields = new Set(['entity', formatFieldPath, 'displayName', 'kpiScenario']);
export function getKpiFormFields(
  form: IForm<IField<unknown>>,
  methods: UseFormReturn<IField<unknown>>,
  flags: MaggieFeatureFlags
) {
  if (!methods) {
    return form;
  }
  const dynamicForm = cloneDeep(form);
  syncFormatDependentFields(dynamicForm, methods);
  syncFormatterField(dynamicForm, methods);
  syncPeriodsWithScenario(dynamicForm, methods);
  if (flags.showAggFuncInKpiConfig) {
    syncAggFuncField(dynamicForm, methods);
  }

  // users can create only custom KPIs
  if (!isEditMode(methods.watch('id') ?? form.data?.id) && dynamicForm.fields.get('entity')) {
    methods.setValue('entity', FieldEntity.userKPI);
    dynamicForm.fields.set('entity', { ...dynamicForm.fields.get('entity')!, disabled: true });
  }

  if (isEditMode(methods.watch('id') ?? form.data?.id)) {
    dynamicForm.fields.set(formatFieldPath, {
      ...dynamicForm.fields.get(formatFieldPath)!,
      disabled: true,
    });
    if (methods.watch('entity') === FieldEntity.systemKPI) {
      SystemKpiUneditableFields.forEach((field) => {
        dynamicForm.fields.set(field, {
          ...dynamicForm.fields.get(field)!,
          disabled: true,
        });
      });
    }
  }
  return dynamicForm;
}

/**
 * With showFieldsManager on, grid columns are governed by fieldDisplayConfig,
 * so the Period picker is hidden and periods stay purely a computation
 * setting. Create mode pins them per scenario (endOfPeriod → latest;
 * otherwise month/quarter/year); edit mode leaves the stored value untouched.
 */
export function hideKpiPeriodsField(form: IForm<IField<unknown>>, methods: UseFormReturn<IField<unknown>>) {
  form.fields.delete('periods');
  if (isEditMode(methods.watch('id') ?? form.data?.id)) {
    return;
  }
  const scenario = methods.watch('kpiScenario') ?? form.data?.kpiScenario;
  const target =
    scenario === KpiConfigScenario.EndOfPeriod
      ? [KpiConfigPeriod.Latest]
      : [KpiConfigPeriod.Month, KpiConfigPeriod.Quarter, KpiConfigPeriod.Year];
  const current = methods.getValues('periods') ?? [];
  if (current.length !== target.length || target.some((period) => !current.includes(period))) {
    methods.setValue('periods', target);
  }
}

export const UniversalKpiEditableFields = new Set(['displayName', 'description', 'kpiGroupId']);

/**
 * Disable every field except the ones a universal KPI may edit (displayName, description,
 * kpiGroupId). Single source of truth for both KPI config forms — mirrors the backend's
 * universal-KPI PUT contract (FD-3958), so the admin and schema forms cannot diverge.
 */
export function lockUniversalKpiFields(form: IForm<IField<unknown>>) {
  for (const [key] of form.fields) {
    if (!UniversalKpiEditableFields.has(key)) {
      form.fields.set(key, { ...form.fields.get(key)!, disabled: true });
    }
  }
}

/**
 * Consolidated clients: an existing universal KPI may only edit
 * displayName, description, and kpiGroupId. No-op otherwise.
 */
export function applyUniversalKpiRestrictions(
  form: IForm<IField<unknown>>,
  methods: UseFormReturn<IField<unknown>>,
  isConsolidated: boolean
) {
  if (!isConsolidated) {
    return;
  }
  // `id` is not a registered form field, so it is absent from watch — read it from form.data,
  // the same way getKpiFormFields determines edit mode. create mode: entity is forced to userKPI,
  // so universal KPIs only exist when editing.
  const id = methods.watch('id') ?? form.data?.id;
  if (id == null) {
    return;
  }
  if ((methods.watch('entity') ?? form.data?.entity) !== FieldEntity.universalKPI) {
    return;
  }

  lockUniversalKpiFields(form);
}

/**
 * Consolidated clients never manage the universal KPI type manually (universal KPIs are
 * owned by Starbreaker). Lock the Type field so a KPI can't be created as — or converted
 * to — universal. Create already forces userKPI; this also covers editing a custom KPI.
 */
export function lockKpiTypeForConsolidated(form: IForm<IField<unknown>>, isConsolidated: boolean) {
  if (!isConsolidated) {
    return;
  }
  const entityField = form.fields.get('entity');
  if (entityField) {
    form.fields.set('entity', { ...entityField, disabled: true });
  }
}

/**
 * Admin Panel KPI config transformer: shared field logic plus the consolidated-client
 * restrictions on universal KPIs. Reads the client config directly from the store since it
 * is effectively static per session.
 */
export function getAdminKpiFormFields(
  form: IForm<IField<unknown>>,
  methods: UseFormReturn<IField<unknown>>,
  flags: MaggieFeatureFlags
) {
  const dynamicForm = getKpiFormFields(form, methods, flags);
  const isConsolidated =
    getForesightStore().get(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection === true;
  applyUniversalKpiRestrictions(dynamicForm, methods, isConsolidated);
  lockKpiTypeForConsolidated(dynamicForm, isConsolidated);
  return dynamicForm;
}

function isEditMode(id: number | undefined) {
  return id != undefined;
}

function syncAggFuncField(form: IForm<IField<unknown>>, methods: UseFormReturn<IField<unknown>>) {
  const aggFuncField = form.fields.get('gridMeta.aggFunc');
  if (isNumericRenderer(methods.watch(formatFieldPath) as RendererType)) {
    form.fields.set('gridMeta.aggFunc', field3ToFormField(getAggFuncField()));
  } else {
    methods.setValue('gridMeta.aggFunc', null);
    if (aggFuncField) {
      form.fields.delete('gridMeta.aggFunc');
    }
  }
}

// null, not undefined: react-hook-form reads an undefined nested value through to the form's
// initial values, so the stored id would come straight back and be cleared again every render.
const CLEARED_FORMATTER = null as never;

/**
 * Fills the Format field's options from the currently chosen Type.
 *
 * A Type whose renderer has no configurable formatter family — text — gets no Format control at
 * all, matching what the shared picker does everywhere else. Changing Type to one the current
 * Format cannot serve drops back to that Type's derived default rather than leaving a format
 * that would not apply.
 */
function syncFormatterField(form: IForm<IField<unknown>>, methods: UseFormReturn<IField<unknown>>) {
  // Fill the slot the field list declared; never invent one the form did not ask for.
  if (!form.fields.has(formatterFieldPath)) {
    return;
  }
  const renderer = methods.watch(formatFieldPath) as RendererType;
  const value = methods.watch(formatterFieldPath) as string | undefined;

  const formatterField = buildFormatterFormField({
    formatterModelsByType: getForesightStore().get(formatterModelsByTypeAtom),
    key: formatterFieldPath,
    label: 'Format',
    renderer,
    value,
  });

  if (!formatterField) {
    // Clear as well as hide: a stored id the new Type cannot serve would be saved anyway by
    // transformKpi, and would resurface as a chosen format if the Type were switched back.
    if (value) {
      methods.setValue(formatterFieldPath, CLEARED_FORMATTER);
    }
    form.fields.delete(formatterFieldPath);
    return;
  }

  // Seed the Type's default when nothing is chosen, and substitute it when the stored id is not
  // one the picker offers — which is how a field stamped with the unregistered `number` came to
  // show an empty Format. Both land on a real option, so the control is never blank.
  const choices = (formatterField.rendererMeta as ISelectMeta<string> | undefined)?.values ?? [];
  if (!value || !choices.some((choice) => choice.value === value)) {
    const preferred = defaultFormatterIdForRenderer(renderer) ?? rendererToFormatterId(renderer);
    methods.setValue(
      formatterFieldPath,
      (choices.some((choice) => choice.value === preferred) ? preferred : CLEARED_FORMATTER) as never
    );
  }

  form.fields.set(formatterFieldPath, formatterField);
}

function syncPeriodsWithScenario(form: IForm<IField<unknown>>, methods: UseFormReturn<IField<unknown>>) {
  const format = methods.watch(formatFieldPath) as RendererType;
  if (format === RendererType.text || format === RendererType.fileUpload) {
    return;
  }
  const scenario = methods.watch('kpiScenario');
  const allowedValues =
    scenario === KpiConfigScenario.EndOfPeriod ? allowedValuesForEndOfPeriod : allowedValuesForOtherScenarios;

  methods.setValue('periods', methods.getValues('periods')?.filter((v) => allowedValues.has(v)) ?? []);

  form.fields.set('periods', {
    ...form.fields.get('periods')!,
    rendererMeta: {
      ...(form.fields.get('periods')?.rendererMeta ?? {}),
      values: Object.entries(KpiConfigPeriodToName).reduce((acc, [key, value]) => {
        if (allowedValues.has(key as KpiConfigPeriod)) {
          acc.push({
            value: key,
            displayName: value,
          });
        }
        return acc;
      }, [] as ISimpleChoice<string>[]),
    },
  });
}
function syncFormatDependentFields(form: IForm<IField<unknown>>, methods: UseFormReturn<IField<unknown>>) {
  const { watch, setValue, getValues } = methods;
  const format = watch(formatFieldPath) as RendererType;

  if (format !== RendererType.text && format !== RendererType.fileUpload) {
    form.fields.set('periods', { ...form.fields.get('periods')!, disabled: false });
    form.fields.set('kpiScenario', { ...form.fields.get('kpiScenario')!, disabled: false });
    // starbreaker clients will set actual/budget/both at config level instead of at individual KPI level
    if (form.fields.get('sectionType')) {
      form.fields.set('sectionType', { ...form.fields.get('sectionType')!, disabled: false });
    }
  } else {
    const periods = getValues('periods');
    if (periods?.length != 1 && periods?.at(0) != KpiConfigPeriod.Latest) {
      setValue('periods', [KpiConfigPeriod.Latest]);
    }
    form.fields.set('periods', { ...form.fields.get('periods')!, disabled: true });

    if (getValues('kpiScenario') != KpiConfigScenario.EndOfPeriod) {
      setValue('kpiScenario', KpiConfigScenario.EndOfPeriod);
    }
    form.fields.set('kpiScenario', { ...form.fields.get('kpiScenario')!, disabled: true });
  }
}
